import React, { useState, memo, useEffect, useRef } from 'react';
import { Text, MenuTrigger, ActionButton, View } from '@adobe/react-spectrum';
import { parseDate, CalendarDate } from '@internationalized/date';
import { calendarDateToISOString } from '../../../components/RsdForms/CreateMoment/utils/dateUtils';
import styles from '../Field/Field.module.scss';
import slideOutStyles from '../SlideOutPanel.module.scss';
import { CalendarRange } from '../../Fields/Calendar';
import Switch from '../Switch/Switch';
import '../../../components/Fields/Calendar/calendar.module.scss';
import { SHOW_ON_THE_ROADMAP_TOOLTIP_MESSAGE } from 'utils/constants';
import {
  getYyyyMmDdInTimeZone,
  isFiscalQuarterInPast
} from '../../../../utils/time';

interface SimpleDateFieldProps {
  field: {
    label: string;
    required?: boolean;
  };
  value:
    | { start?: string; end?: string }
    | { type: 'months'; months: string[] }
    | { type: 'quarters'; quarters: string[] }
    | { type: 'special'; value: string }
    | string // Support plain string values like "TBD" or "N/A"
    | null;
  onChange: (
    value:
      | { type: 'date'; date: string }
      | { type: 'months'; months: string[] }
      | { type: 'quarters'; quarters: string[] }
      | { type: 'special'; value: string }
  ) => void;
  isSingleDate?: boolean;
  // Toggle props for "Show on the Roadmap" functionality
  showToggle?: boolean;
  toggleChecked?: boolean;
  toggleDisabled?: boolean;
  onToggleChange?: (checked: boolean) => void;
  toggleLabel?: string;
  // Calendar options
  disableTBD?: boolean;
  disableNA?: boolean;
  // RBAC
  disabled?: boolean; // Disable date editing when user doesn't have permission
  // Secondary Date Status, read for the overdue colouring only. The Active/Completed
  // control itself lives in the phase status menu, independent of "Show Additional Card".
  secondaryDateStatus?: 'ACTIVE' | 'COMPLETED' | null;
  // Status props for overdue determination
  primaryStatus?: string; // reportingStatus for Marketing Moment date
  fieldId?: string; // 'marketingMoment', 'releaseDate', or 'effectiveDate'
  // Data attributes for DOM targeting (e.g., alert highlighting)
  'data-testid'?: string;
  highlightIssue?: {
    issueId: string;
    fieldRef: string | null;
    ruleId: string;
  } | null;
}

/**
 * SimpleDateField: Displays formatted dates that match Figma design
 * Shows "Jan 22, 2025" format and opens calendar overlay when clicked
 */
export const SimpleDateField: React.FC<SimpleDateFieldProps> = memo(
  ({
    field,
    value,
    onChange,
    isSingleDate = false,
    disabled = false,
    showToggle = false,
    toggleChecked = false,
    toggleDisabled = false,
    onToggleChange,
    toggleLabel,
    disableTBD = false,
    disableNA = true,
    secondaryDateStatus,
    primaryStatus,
    fieldId,
    'data-testid': dataTestId,
    highlightIssue
  }) => {
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [dateRange, setDateRange] = useState<{
      start?: CalendarDate;
      end?: CalendarDate;
    }>({});
    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);

    // Initialize date range from value
    useEffect(() => {
      // Skip date range initialization for plain strings (TBD, N/A)
      if (typeof value === 'string') {
        setDateRange({ start: undefined, end: undefined });
        return;
      }

      if (value && 'type' in value && value.type === 'months') {
        setDateRange({ start: undefined, end: undefined });
      } else if (
        value &&
        typeof value === 'object' &&
        (value as { start?: string })?.start &&
        (value as { end?: string })?.end
      ) {
        setDateRange({
          start: parseDate((value as { start: string }).start),
          end: parseDate((value as { end: string }).end)
        });
      } else {
        setDateRange({ start: undefined, end: undefined });
      }
    }, [value]);

    // Handle click outside to close calendar
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as HTMLElement;

        // Check if click is inside calendar elements (quarters/months/years buttons)
        const isCalendarButton =
          target.closest('.quarters-list') ||
          target.closest('.months-list') ||
          target.closest('.years-list') ||
          target.closest('.calendar-container') ||
          target.closest('[data-testid*="calendar-"]');

        if (isCalendarButton) {
          // Don't close if clicking calendar buttons
          return;
        }

        if (containerRef.current && !containerRef.current.contains(target)) {
          setIsCalendarOpen(false);
        }
      };

      if (isCalendarOpen) {
        // Use capture phase so stopPropagation() on child buttons doesn't block this handler
        document.addEventListener('mousedown', handleClickOutside, true);
        return () =>
          document.removeEventListener('mousedown', handleClickOutside, true);
      }
    }, [isCalendarOpen]);

    // Helper to format CalendarDate to ISO string - use timezone-safe utility
    const toISO = (date: CalendarDate) => calendarDateToISOString(date);

    // Helper to format date like "Jan 22, 2025" as shown in Figma
    // Uses timezone-safe formatting to prevent date shifts
    const formatDateDisplay = (date: CalendarDate) => {
      const utcDate = new Date(Date.UTC(date.year, date.month - 1, date.day));
      return utcDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC' // Force UTC to prevent timezone conversion
      });
    };

    // Helper to check if a date is in the past (before today in the user's timezone).
    // Compare YYYY-MM-DD strings so "today" matches the system calendar (avoids UTC vs local mismatch).
    const isDateInPast = (): boolean => {
      const todayYmd = getYyyyMmDdInTimeZone();
      const today = new Date();
      today.setHours(0, 0, 0, 0); // local midnight, for fiscal quarter helper only

      // Handle plain string values like "TBD" or "N/A" - these are not past dates
      if (typeof value === 'string') {
        return false;
      }

      // Handle special values like TBD - not past dates
      if (
        value &&
        typeof value === 'object' &&
        'type' in value &&
        value.type === 'special'
      ) {
        return false;
      }

      // Handle months format (e.g., "Oct 2025")
      if (
        value &&
        typeof value === 'object' &&
        'type' in value &&
        value.type === 'months' &&
        value.months?.length > 0
      ) {
        // Parse "Oct 2025" format - past only after the month ends (in user's calendar)
        const match = value.months[0].match(/([A-Za-z]+)\s+(\d{4})/);
        if (match) {
          const monthName = match[1];
          const year = parseInt(match[2], 10);
          const monthIndex = new Date(`${monthName} 1, ${year}`).getMonth();
          const lastDayOfMonth = new Date(year, monthIndex + 1, 0);
          const fieldEndYmd = `${lastDayOfMonth.getFullYear()}-${String(
            lastDayOfMonth.getMonth() + 1
          ).padStart(2, '0')}-${String(lastDayOfMonth.getDate()).padStart(
            2,
            '0'
          )}`;
          return fieldEndYmd < todayYmd;
        }
        return false;
      }

      // Handle quarters format (e.g., "Q1 2025")
      if (
        value &&
        typeof value === 'object' &&
        'type' in value &&
        value.type === 'quarters' &&
        value.quarters?.length > 0
      ) {
        return isFiscalQuarterInPast(value.quarters[0], today);
      }

      // Handle regular dates
      if (dateRange?.start) {
        const d = dateRange.start;
        const fieldYmd = `${d.year}-${String(d.month).padStart(
          2,
          '0'
        )}-${String(d.day).padStart(2, '0')}`;
        return fieldYmd < todayYmd;
      }

      return false;
    };

    // Helper to check if date should be shown in red (overdue)
    // A date is overdue if it's in the past AND status is ACTIVE (not COMPLETED)
    const isDateOverdue = (): boolean => {
      // First check if date is in the past
      if (!isDateInPast()) {
        return false; // Not in past, so not overdue
      }

      // Date is in past, now check status based on field type
      if (
        fieldId === 'marketingMoment' ||
        fieldId === 'customerNotificationDate'
      ) {
        // Primary phase date: show red only if past and phase is not COMPLETED
        const normalizedStatus = primaryStatus?.toUpperCase();
        return (
          normalizedStatus !== 'COMPLETED' && normalizedStatus !== 'COMPLETE'
        );
      } else if (fieldId === 'releaseDate' || fieldId === 'effectiveDate') {
        // For Release/Effective Date: secondaryDateStatus is the sole, user-managed gate for
        // this alert, deliberately independent of the "Show Additional Card" toggle in every
        // case — matching the backend's DatePastDueAlertRule, which was made independent of
        // showOnRoadmap for the same reason. toggleChecked is never consulted here.
        const normalizedPrimaryStatus = primaryStatus?.toUpperCase();

        // When the Active/Completed dropdown is not used (e.g. "Product Release without Marketing"
        // has no Show on Roadmap / secondary status), `secondaryDateStatus` is not passed.
        // Treat like marketing date: overdue if in the past and phase is not complete.
        if (secondaryDateStatus == null) {
          return (
            normalizedPrimaryStatus !== 'COMPLETED' &&
            normalizedPrimaryStatus !== 'COMPLETE'
          );
        }
        // secondaryDateStatus alone decides — show red only if ACTIVE
        return secondaryDateStatus === 'ACTIVE';
      }

      // Fallback: if we don't have field ID, use old behavior (show red for past dates)
      return true;
    };

    const getDisplayValue = () => {
      // Handle plain string values like "TBD" or "N/A"
      if (typeof value === 'string') {
        return value;
      }

      // Handle special values like TBD (new format)
      if (
        value &&
        typeof value === 'object' &&
        'type' in value &&
        value.type === 'special' &&
        'value' in value
      ) {
        return value.value; // Return 'TBD' or other special values
      }

      // Handle months format (e.g., "Oct 2025")
      if (
        value &&
        typeof value === 'object' &&
        'type' in value &&
        value.type === 'months' &&
        value.months?.length > 0
      ) {
        return value.months[0];
      }

      // Handle quarters format (e.g., "Q1 2025")
      if (
        value &&
        typeof value === 'object' &&
        'type' in value &&
        value.type === 'quarters' &&
        value.quarters?.length > 0
      ) {
        return value.quarters[0];
      }

      // Handle date ranges
      if (dateRange?.start) {
        if (isSingleDate) {
          return formatDateDisplay(dateRange.start);
        } else {
          return dateRange?.end
            ? `${formatDateDisplay(dateRange.start)} - ${formatDateDisplay(
                dateRange.end
              )}`
            : formatDateDisplay(dateRange.start);
        }
      }
      return 'N/A';
    };

    // Helper to extract default special value for calendar
    const getDefaultSpecialValue = (): 'TBD' | 'N/A' | undefined => {
      // Handle plain string values like "TBD" or "N/A"
      if (typeof value === 'string' && (value === 'TBD' || value === 'N/A')) {
        return value as 'TBD' | 'N/A';
      }

      // Handle special format { type: 'special', value: 'TBD' }
      if (
        value &&
        typeof value === 'object' &&
        'type' in value &&
        value.type === 'special' &&
        'value' in value
      ) {
        return value.value as 'TBD' | 'N/A';
      }

      return undefined;
    };

    // Helper to extract default quarter for calendar
    const getDefaultQuarter = ():
      | { quarter: number; year: number }
      | undefined => {
      if (
        value &&
        typeof value === 'object' &&
        'type' in value &&
        value.type === 'quarters' &&
        value.quarters?.length > 0
      ) {
        // Parse "Q2 2026" format
        const match = value.quarters[0].match(/Q(\d)\s+(\d{4})/);
        if (match) {
          return {
            quarter: parseInt(match[1], 10),
            year: parseInt(match[2], 10)
          };
        }
      }
      return undefined;
    };

    // Helper to extract default month for calendar
    const getDefaultMonth = (): { month: string; year: number } | undefined => {
      if (
        value &&
        typeof value === 'object' &&
        'type' in value &&
        value.type === 'months' &&
        value.months?.length > 0
      ) {
        // Parse "Feb 2026" format
        const match = value.months[0].match(/([A-Za-z]+)\s+(\d{4})/);
        if (match) {
          return { month: match[1], year: parseInt(match[2], 10) };
        }
      }
      return undefined;
    };

    const handleRangeChange = (newRange: {
      start: CalendarDate;
      end: CalendarDate;
    }) => {
      setDateRange(newRange);
      if (newRange.start) {
        if (isSingleDate) {
          // For single date picker, use only the start date
          const payload = {
            type: 'date',
            date: toISO(newRange.start)
          };
          onChange(payload as { type: 'date'; date: string });
          setIsCalendarOpen(false); // Close immediately for single date
        } else if (newRange.end) {
          // For date range picker, report the start date as the single date (per slideout needs)
          const payload = {
            type: 'date',
            date: toISO(newRange.start)
          };
          onChange(payload as { type: 'date'; date: string });
          setIsCalendarOpen(false); // Close when range is complete
        }
      }
    };

    // Handle single date selection from CalendarRange in single mode
    const handleDateChange = (
      newDate: CalendarDate | null,
      isQuarterSelection?: boolean,
      selectedQuarter?: { quarter: number; year: number },
      isMonthSelection?: boolean,
      selectedMonth?: { month: string; year: number },
      specialValue?: 'TBD' | 'N/A' | null
    ) => {
      if (!newDate && !isQuarterSelection && !isMonthSelection && !specialValue)
        return;

      // Handle special values first
      if (specialValue !== undefined) {
        if (specialValue === null) {
          // When TBD/N/A is unchecked, don't close the calendar or send changes
          // Keep calendar open so user can select a valid date
          setDateRange({ start: undefined, end: undefined });
          // Don't close calendar, don't call onChange
          // User must select a valid date/month/quarter before changes are saved
          return;
        }
        // For special values like TBD or N/A, save them as special type
        onChange({ type: 'special', value: specialValue } as {
          type: 'special';
          value: string;
        });
        setDateRange({ start: undefined, end: undefined });
        setIsCalendarOpen(false);
        return;
      }

      // Handle quarter selection
      if (isQuarterSelection && selectedQuarter) {
        const payload = {
          type: 'quarters',
          quarters: [`Q${selectedQuarter.quarter} ${selectedQuarter.year}`]
        };
        onChange(payload as { type: 'quarters'; quarters: string[] });
        setDateRange({ start: undefined, end: undefined });
        setIsCalendarOpen(false);
        return;
      }

      // Handle month selection
      if (isMonthSelection && selectedMonth) {
        const payload = {
          type: 'months',
          months: [`${selectedMonth.month} ${selectedMonth.year}`]
        };
        onChange(payload as { type: 'months'; months: string[] });
        setDateRange({ start: undefined, end: undefined });
        setIsCalendarOpen(false);
        return;
      }

      // Handle regular date selection
      if (newDate) {
        setDateRange({ start: newDate, end: newDate });
        const payload = { type: 'date', date: toISO(newDate) };
        onChange(payload as { type: 'date'; date: string });
        setIsCalendarOpen(false);
      }
    };

    return (
      <div
        className={`${styles.fieldContainer} ${slideOutStyles.fieldContainerWithToggle}`}
        ref={containerRef}
      >
        {/* Main field component (matches Figma Frame 26086693/default) */}
        {/* data-testid is on this inner div so alert highlighting borders only this area (label + date) */}
        <div
          className={`${slideOutStyles.fieldComponent}${
            highlightIssue ? ' alert-field-highlight' : ''
          }`}
          data-testid={dataTestId}
          {...(highlightIssue
            ? {
                'data-alert-issue-id': highlightIssue.issueId,
                'data-alert-field-ref': highlightIssue.fieldRef ?? 'null',
                'data-alert-rule-id': highlightIssue.ruleId
              }
            : {})}
        >
          <div className={styles.label}>{field.label}</div>
          <div
            className={`${styles.valueContainer} ${slideOutStyles.valueColumnFixed}`}
            style={{ position: 'relative' }}
          >
            <div
              className={`${styles.value} ${slideOutStyles.clickableDate}`}
              ref={buttonRef}
              onClick={() => {
                if (!disabled) {
                  // Toggle calendar: if already open, close it; if closed, open it
                  setIsCalendarOpen(prev => !prev);
                }
              }}
              style={{
                cursor: disabled ? 'not-allowed' : 'pointer',
                color: isDateOverdue() ? '#EA3829' : undefined // Red color for overdue dates (past + ACTIVE status)
              }}
            >
              {getDisplayValue()}
            </div>
            {isCalendarOpen && (
              <div className={slideOutStyles.calendarDropdown}>
                <CalendarRange
                  defaultDate={dateRange.start || undefined}
                  defaultQuarter={getDefaultQuarter()}
                  defaultMonth={getDefaultMonth()}
                  onCalendarDateChange={handleDateChange}
                  mode="single"
                  skipTriggerButton={true}
                  hideYearList={true}
                  showTBDNA={true}
                  defaultSpecialValue={getDefaultSpecialValue()}
                  disablePastDate={false}
                  disableNA={disableNA}
                  disableTBD={disableTBD}
                  data-testid="slideout-calendar"
                />
              </div>
            )}
          </div>
        </div>

        {/* Switch component as sibling (matches Figma layout) */}
        {/* Always render the container to maintain consistent spacing */}
        <div className={slideOutStyles.switchComponent}>
          {showToggle && (
            <Switch
              checked={toggleChecked}
              disabled={
                disabled // Disable completely if no edit permission (RBAC)
                  ? true
                  : toggleDisabled && !toggleChecked // Otherwise, disable only ON toggle when dates are equal
              }
              onChange={onToggleChange}
              label={toggleLabel || 'Show Additional Card'}
              toolTipMessage={SHOW_ON_THE_ROADMAP_TOOLTIP_MESSAGE}
            />
          )}
          {/* The secondary date's Active/Completed control lives in the phase status menu
              (useSlideoutFieldDropdownPortals), alongside the primary status, so both can be
              managed from one place and neither depends on "Show Additional Card". */}
        </div>
      </div>
    );
  },
  (prevProps, nextProps) =>
    prevProps.value === nextProps.value &&
    prevProps.toggleChecked === nextProps.toggleChecked &&
    prevProps.showToggle === nextProps.showToggle &&
    prevProps.secondaryDateStatus === nextProps.secondaryDateStatus &&
    prevProps.primaryStatus === nextProps.primaryStatus &&
    prevProps.fieldId === nextProps.fieldId &&
    prevProps.highlightIssue?.fieldRef === nextProps.highlightIssue?.fieldRef
);

export default SimpleDateField;
